// Maps Reddit-sub slugs to landing page content. The slug in the URL must match
// the subreddit name (lowercase, no /r/ prefix). E.g. /r/smallbusiness
//
// industryKey aligns with /app/backend/benchmarks.py keys so the Analyze page
// can pre-select the right industry when the visitor clicks the CTA.
export const REDDIT_LANDINGS = {
  smallbusiness: {
    sub: "smallbusiness",
    greeting: "Hey r/smallbusiness 👋",
    headline: "What grade would a CFO give your books?",
    pitch:
      "Drop in your P&L numbers (or upload a CSV) and we'll grade your business A+ to F across margins, debt coverage, liquidity, and cash conversion — using benchmarks pulled from 16 industries. Free, no signup.",
    industryKey: "general",
    benchmarkTeaser: {
      label: "Typical small-business benchmark",
      rows: [
        { k: "Gross margin", v: "30–45%" },
        { k: "Net margin", v: "5–10%" },
        { k: "Current ratio", v: "1.5–2.5×" },
        { k: "DSCR (lender minimum)", v: "≥ 1.25×" },
      ],
    },
    proofPoints: [
      "24+ ratios calculated in seconds",
      "Industry-aware thresholds — not generic",
      "Plain-English CFO memo of what to fix next quarter",
    ],
  },
  entrepreneur: {
    sub: "entrepreneur",
    greeting: "Hey r/Entrepreneur 👋",
    headline: "Is your business actually healthy — or just busy?",
    pitch:
      "Revenue is vanity, profit is sanity, cash is reality. Paste your numbers and we'll show you the 24 ratios bankers and acquirers actually look at — with a letter grade you can screenshot.",
    industryKey: "general",
    benchmarkTeaser: {
      label: "What 'healthy' looks like for most SMBs",
      rows: [
        { k: "EBITDA margin", v: "10–20%" },
        { k: "Debt / EBITDA", v: "< 3.0×" },
        { k: "Cash conv. cycle", v: "30–60 days" },
        { k: "Quick ratio", v: "≥ 1.0×" },
      ],
    },
    proofPoints: [
      "From P&L paste to grade in under 3 minutes",
      "Owner-operator OR analyst lens — pick your framing",
      "Trend tracking once you have 2+ periods",
    ],
  },
  startups: {
    sub: "startups",
    greeting: "Hey r/startups 👋",
    headline: "Get the credit memo a banker would write about your startup.",
    pitch:
      "Whether you're prepping for a raise, a credit line, or a board update — paste your numbers and get an analyst-tier memo with DSCR, debt-to-EBITDA, and runway math. Read it as a founder or as a lender.",
    industryKey: "saas",
    benchmarkTeaser: {
      label: "SaaS / early-stage benchmark",
      rows: [
        { k: "Gross margin (SaaS)", v: "70–85%" },
        { k: "Rule of 40", v: "≥ 40" },
        { k: "Burn multiple", v: "watch closely" },
        { k: "Months of runway", v: "≥ 18 ideal" },
      ],
    },
    proofPoints: [
      "Analyst-lens narrative (think credit committee, not pitch deck)",
      "What-If sandbox: model price/cost changes live",
      "PDF for investors & board",
    ],
  },
  restaurantowners: {
    sub: "restaurantowners",
    greeting: "Hey r/restaurantowners 👋",
    headline: "How does your restaurant compare to the median?",
    pitch:
      "Food cost, labor cost, prime cost, EBITDA — the four numbers that decide whether you make it. Paste yours and we'll grade them against industry medians and show you the gap.",
    industryKey: "restaurant",
    benchmarkTeaser: {
      label: "Industry benchmark — full-service restaurants",
      rows: [
        { k: "Food cost %", v: "28–32%" },
        { k: "Labor cost %", v: "28–35%" },
        { k: "Prime cost", v: "≤ 60% target" },
        { k: "EBITDA margin", v: "10–15%" },
      ],
    },
    proofPoints: [
      "Restaurant-specific KPIs (food, labor, prime cost)",
      "Compare to other restaurants in your region",
      "Letter grade you can share or print",
    ],
  },
  ecommerce: {
    sub: "ecommerce",
    greeting: "Hey r/ecommerce 👋",
    headline: "Your Shopify dashboard hides what really matters.",
    pitch:
      "Revenue is up. But are you actually making money? Drop your P&L numbers and we'll show you contribution margin, cash conversion cycle, debt coverage, and the grade an acquirer would give your store.",
    industryKey: "ecommerce",
    benchmarkTeaser: {
      label: "Industry benchmark — DTC / e-commerce",
      rows: [
        { k: "Gross margin", v: "40–60%" },
        { k: "Contribution margin", v: "20–35%" },
        { k: "Inventory days", v: "60–90 days" },
        { k: "Net margin", v: "5–12%" },
      ],
    },
    proofPoints: [
      "Built for inventory-heavy businesses",
      "Cash conversion cycle in plain English",
      "Acquisition-ready financial summary",
    ],
  },
  shopify: {
    sub: "shopify",
    greeting: "Hey r/Shopify 👋",
    headline: "Beyond the Shopify dashboard — is your store financially healthy?",
    pitch:
      "Shopify shows you revenue and orders. We show you the metrics a buyer or lender would actually score: contribution margin, cash conversion cycle, debt coverage, and a letter grade.",
    industryKey: "ecommerce",
    benchmarkTeaser: {
      label: "Industry benchmark — Shopify / DTC stores",
      rows: [
        { k: "Gross margin", v: "40–60%" },
        { k: "Net margin", v: "5–12%" },
        { k: "Inventory turn", v: "4–6×/yr" },
        { k: "Quick ratio", v: "≥ 1.0×" },
      ],
    },
    proofPoints: [
      "Pre-set for e-commerce industry benchmarks",
      "CSV upload — works with your accounting export",
      "What-If sandbox: model a price hike or COGS drop",
    ],
  },
  amazonfba: {
    sub: "AmazonFBA",
    greeting: "Hey r/AmazonFBA 👋",
    headline: "Is your FBA business actually a business — or a job in disguise?",
    pitch:
      "Amazon fees, ad spend, returns, and inventory tie up real cash. Paste your numbers and get a no-fluff financial grade: margins, cash cycle, and what an aggregator would actually pay.",
    industryKey: "ecommerce",
    benchmarkTeaser: {
      label: "Benchmark — FBA / Amazon sellers",
      rows: [
        { k: "Gross margin (after FBA fees)", v: "30–45%" },
        { k: "Net margin", v: "10–20%" },
        { k: "Inventory days", v: "60–120 days" },
        { k: "DSCR", v: "≥ 1.25× for SBA" },
      ],
    },
    proofPoints: [
      "Numbers an aggregator would underwrite from",
      "Cash-tied-in-inventory analysis built in",
      "PDF — useful for broker conversations",
    ],
  },
  construction: {
    sub: "Construction",
    greeting: "Hey r/Construction 👋",
    headline: "WIP, retention, and debt — is your job-cost structure healthy?",
    pitch:
      "Construction businesses look fine on revenue and broke on cash flow. We grade your gross margin, working capital, DSCR, and debt — using construction-specific benchmarks.",
    industryKey: "construction",
    benchmarkTeaser: {
      label: "Industry benchmark — construction / trades",
      rows: [
        { k: "Gross margin", v: "15–25%" },
        { k: "Net margin", v: "3–7%" },
        { k: "DSCR (for bonding)", v: "≥ 1.5×" },
        { k: "Current ratio", v: "≥ 1.5×" },
      ],
    },
    proofPoints: [
      "Construction-aware benchmarks",
      "DSCR check for bonding & SBA",
      "Letter grade you can send to your surety",
    ],
  },
  sweatystartup: {
    sub: "sweatystartup",
    greeting: "Hey r/sweatystartup 👋",
    headline: "Is your service business actually scaling — or just spinning?",
    pitch:
      "HVAC, landscaping, cleaning, plumbing — service businesses live or die on margin and labor cost. Paste your numbers and we'll grade you against industry medians.",
    industryKey: "personal_services",
    benchmarkTeaser: {
      label: "Benchmark — service / trades",
      rows: [
        { k: "Gross margin", v: "35–55%" },
        { k: "Labor as % of revenue", v: "25–40%" },
        { k: "Net margin", v: "8–15%" },
        { k: "DSCR for SBA", v: "≥ 1.25×" },
      ],
    },
    proofPoints: [
      "Plain-English memo: what to fix this quarter",
      "Built for owner-operators",
      "SBA-friendly DSCR check included",
    ],
  },
  accounting: {
    sub: "Accounting",
    greeting: "Hey r/Accounting 👋",
    headline: "A fast credit memo for any client P&L you can paste.",
    pitch:
      "Built for accountants and analysts: paste 6 lines from a client's books and get a full credit-memo-style readout — DSCR, debt-to-EBITDA, liquidity, and an analyst-lens narrative.",
    industryKey: "general",
    benchmarkTeaser: {
      label: "What the tool surfaces",
      rows: [
        { k: "Metrics computed", v: "24+" },
        { k: "Industry benchmarks", v: "16 sectors × 15 regions" },
        { k: "Audience lenses", v: "Owner / Analyst" },
        { k: "Export", v: "PDF" },
      ],
    },
    proofPoints: [
      "Analyst / lender narrative tone",
      "Industry-aware thresholds, not generic",
      "Use it client-side or just as a sanity check",
    ],
  },
  bookkeeping: {
    sub: "Bookkeeping",
    greeting: "Hey r/Bookkeeping 👋",
    headline: "A quick health check for any client's books.",
    pitch:
      "Want to add a 'financial health review' to your service menu? Paste a client's P&L and balance sheet and our tool grades 24 ratios in seconds. Bring it to your next client meeting.",
    industryKey: "general",
    benchmarkTeaser: {
      label: "Why bookkeepers use this",
      rows: [
        { k: "Setup time", v: "0 — paste & go" },
        { k: "Output", v: "Letter grade + memo" },
        { k: "Coverage", v: "Margins, debt, liquidity" },
        { k: "Cost", v: "Free (paid PDF if you want branding)" },
      ],
    },
    proofPoints: [
      "Add a 'health check' offering to your service menu",
      "PDF export for client deliverables",
      "Industry-aware benchmarks",
    ],
  },
  sideproject: {
    sub: "SideProject",
    greeting: "Hey r/SideProject 👋",
    headline: "I built a tool that grades any business A+ to F. Would love feedback.",
    pitch:
      "CFO Grade takes your P&L and balance sheet (paste or CSV) and gives you a letter grade across 24 financial ratios with an AI-generated CFO memo. Free to use. Feedback welcome.",
    industryKey: "general",
    benchmarkTeaser: {
      label: "Under the hood",
      rows: [
        { k: "Frontend", v: "React + Tailwind" },
        { k: "Backend", v: "FastAPI + MongoDB" },
        { k: "AI", v: "Claude Sonnet 4.5" },
        { k: "Payments", v: "Stripe (test mode)" },
      ],
    },
    proofPoints: [
      "Try the live sample data in one click",
      "Free analysis, optional paid PDF & Pro Pass",
      "Comments / roasts welcome — DM or email",
    ],
  },
};

export function getRedditLanding(slugRaw) {
  if (!slugRaw) return null;
  const slug = String(slugRaw).toLowerCase();
  return REDDIT_LANDINGS[slug] || null;
}

export const REDDIT_LANDING_SLUGS = Object.keys(REDDIT_LANDINGS);
